import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Pencil, Plus } from "lucide-react";

import { disciplineService, type BehaviorCategory, type DisciplinaryAction } from "../../services/disciplineService";
import { apiErrorMessage } from "../../utils/apiError";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { SANCTION_TYPES, SEVERITIES, definitionConfig, labelFrom, severityLabel } from "./disciplineLogic";

const active = (row: { is_active: boolean }) => (row.is_active ? { label: "In use", dot: "bg-emerald-500" } : { label: "Retired", dot: "bg-gray-400" });

function SeveritySelect({ name, value }: { name: string; value?: number }) {
  return (
    <select name={name} defaultValue={String(value ?? 2)} className="input mt-1">
      {SEVERITIES.map(([key, label]) => <option key={key} value={key}>{key} · {label}</option>)}
    </select>
  );
}

function CategoryForm({ category, onClose, onDone }: { category?: BehaviorCategory; onClose: () => void; onDone: (row: BehaviorCategory) => void }) {
  const save = useMutation({
    mutationFn: (data: Partial<BehaviorCategory>) => (category ? disciplineService.updateBehaviorCategory({ id: category.id, data }) : disciplineService.createBehaviorCategory({ ...data, is_active: true })),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({ name: String(data.get("name")).trim(), description: String(data.get("description") ?? "").trim(), severity_level: Number(data.get("severity_level")), color_code: String(data.get("color_code")) });
  };
  return (
    <FormModal id="behaviour-kind" title={category ? `Change ${category.name}` : "New kind of behaviour"} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "It could not be saved.") : undefined} saveLabel={category ? "Save changes" : "Add it"} note={category?.incident_count ? "Past cases keep the severity they were given." : undefined} onClose={onClose} onSubmit={submit}>
      <FormSection title="The kind of behaviour" divided={false}>
        <Field label="Name" hint="e.g. Fighting, Bullying, Lateness, Exam malpractice.">
          <input required name="name" maxLength={100} defaultValue={category?.name} className="input mt-1" />
        </Field>
        <Field label="Severity" hint="New cases of this kind start at this level.">
          <SeveritySelect name="severity_level" value={category?.severity_level} />
        </Field>
        <Field label="What it covers" optional className="sm:col-span-2">
          <textarea name="description" rows={2} defaultValue={category?.description} className="input mt-1" />
        </Field>
        <Field label="Colour" hint="Marks it in the lists.">
          <input type="color" name="color_code" defaultValue={category?.color_code ?? "#f59e0b"} className="mt-1 h-10 w-16 rounded border border-gray-300" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function SanctionForm({ sanction, onClose, onDone }: { sanction?: DisciplinaryAction; onClose: () => void; onDone: (row: DisciplinaryAction) => void }) {
  const save = useMutation({
    mutationFn: (data: Partial<DisciplinaryAction>) => (sanction ? disciplineService.updateDisciplinaryAction({ id: sanction.id, data }) : disciplineService.createDisciplinaryAction({ ...data, is_active: true })),
    onSuccess: onDone,
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({
      name: String(data.get("name")).trim(),
      action_type: String(data.get("action_type")),
      description: String(data.get("description") ?? "").trim(),
      severity_level: Number(data.get("severity_level")),
      duration_days: Number(data.get("duration_days")) || 0,
      requires_parent_notification: data.get("requires_parent_notification") === "on",
      requires_principal_approval: data.get("requires_principal_approval") === "on",
    });
  };
  return (
    <FormModal id="conduct-action" title={sanction ? `Change ${sanction.name}` : "New action"} busy={save.isPending} error={save.isError ? apiErrorMessage(save.error, "It could not be saved.") : undefined} saveLabel={sanction ? "Save changes" : "Add it"} note="Actions at severity 4 or 5 always need leadership approval." onClose={onClose} onSubmit={submit}>
      <FormSection title="The action" divided={false}>
        <Field label="Name" hint="e.g. Written warning, Two-day suspension, Parent meeting.">
          <input required name="name" maxLength={100} defaultValue={sanction?.name} className="input mt-1" />
        </Field>
        <Field label="Kind">
          <select name="action_type" defaultValue={sanction?.action_type ?? "written_warning"} className="input mt-1">
            {SANCTION_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </Field>
        <Field label="Severity">
          <SeveritySelect name="severity_level" value={sanction?.severity_level} />
        </Field>
        <Field label="Lasts (days)" hint="0 when it is not timed.">
          <input type="number" min={0} name="duration_days" defaultValue={sanction?.duration_days ?? 0} className="input mt-1" />
        </Field>
        <Field label="What it involves" optional className="sm:col-span-2">
          <textarea name="description" rows={2} defaultValue={sanction?.description} className="input mt-1" />
        </Field>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="requires_parent_notification" defaultChecked={sanction?.requires_parent_notification ?? true} className="h-4 w-4 rounded border-gray-300" /> The parent must be told
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" name="requires_principal_approval" defaultChecked={sanction?.requires_principal_approval ?? false} className="h-4 w-4 rounded border-gray-300" /> Leadership must approve it
        </label>
      </FormSection>
    </FormModal>
  );
}

/** The kinds of behaviour and the actions a case can propose. Leadership sets them; everyone reads them. */
export function DefinitionsPanel({ categories, sanctions, isLoading, isError, onRetry, canEdit, onChanged }: {
  categories?: BehaviorCategory[];
  sanctions?: DisciplinaryAction[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<{ kind: "category"; row: BehaviorCategory } | { kind: "sanction"; row: DisciplinaryAction } | null>(null);
  const [editing, setEditing] = useState<{ kind: "category"; row?: BehaviorCategory } | { kind: "sanction"; row?: DisciplinaryAction } | null>(null);
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    setViewing(null);
    onChanged();
  };
  const retire = useMutation({
    mutationFn: (target: NonNullable<typeof viewing>): Promise<{ name: string; is_active: boolean }> =>
      target.kind === "category"
        ? disciplineService.updateBehaviorCategory({ id: target.row.id, data: { is_active: !target.row.is_active } })
        : disciplineService.updateDisciplinaryAction({ id: target.row.id, data: { is_active: !target.row.is_active } }),
    onSuccess: (row) => done(`${row.name} is ${row.is_active ? "in use again" : "retired: past cases keep it, new ones cannot use it"}.`),
  });

  const categoryColumns: RegisterColumn<BehaviorCategory>[] = [
    {
      header: "Kind of behaviour",
      cell: (row) => (
        <span className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: row.color_code }} aria-hidden />
          <span className="font-medium text-gray-900">{row.name}</span>
        </span>
      ),
      exportValue: (row) => row.name,
    },
    { header: "Severity", cell: (row) => severityLabel(row.severity_level), exportValue: (row) => severityLabel(row.severity_level), className: "whitespace-nowrap text-gray-700" },
    { header: "What it covers", cell: (row) => row.description || "—", exportValue: (row) => row.description, className: "max-w-[22rem] truncate text-gray-700" },
    { header: "Cases", cell: (row) => row.incident_count, exportValue: (row) => row.incident_count, align: "right", className: "tabular-nums" },
    { header: "Status", cell: (row) => <StatusDot {...active(row)} />, exportValue: (row) => active(row).label },
  ];
  const sanctionColumns: RegisterColumn<DisciplinaryAction>[] = [
    { header: "Action", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Kind", cell: (row) => labelFrom(SANCTION_TYPES, row.action_type), exportValue: (row) => labelFrom(SANCTION_TYPES, row.action_type), className: "text-gray-700" },
    { header: "Severity", cell: (row) => severityLabel(row.severity_level), exportValue: (row) => severityLabel(row.severity_level), className: "whitespace-nowrap text-gray-700" },
    { header: "Lasts", cell: (row) => (row.duration_days ? `${row.duration_days} days` : "—"), exportValue: (row) => row.duration_days, className: "text-gray-700" },
    { header: "Parent told", cell: (row) => (row.requires_parent_notification ? "Yes" : "No"), exportValue: (row) => (row.requires_parent_notification ? "Yes" : "No"), className: "text-gray-700" },
    { header: "Approval", cell: (row) => (row.requires_principal_approval || row.severity_level >= 4 ? "Leadership" : "—"), exportValue: (row) => (row.requires_principal_approval || row.severity_level >= 4 ? "Leadership" : ""), className: "text-gray-700" },
    { header: "Cases", cell: (row) => row.incident_count, exportValue: (row) => row.incident_count, align: "right", className: "tabular-nums" },
    { header: "Status", cell: (row) => <StatusDot {...active(row)} />, exportValue: (row) => active(row).label },
  ];
  const addButton = (kind: "category" | "sanction", label: string) =>
    canEdit && (
      <button type="button" onClick={() => setEditing({ kind })} className="btn-primary h-8 min-h-8 px-3 text-xs">
        <Plus className="mr-1.5 h-3.5 w-3.5" /> {label}
      </button>
    );

  return (
    <div className="space-y-6">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {!canEdit && <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">School leadership sets these. They are here so everyone uses them the same way.</p>}
      <ClientRegister
        title="Kinds of behaviour"
        description="What a case is about, and how serious it is."
        actions={addButton("category", "New kind")}
        rows={categories}
        config={definitionConfig<BehaviorCategory>()}
        columns={categoryColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => { retire.reset(); setViewing({ kind: "category", row }); }}
        noun={["kind", "kinds"]}
        exportName={{ file: "behaviour-kinds", title: "Kinds of Behaviour" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canEdit ? "None yet. Add the first, e.g. Lateness or Fighting." : "Leadership has not set any up yet."}
      />
      <ClientRegister
        title="Actions"
        description="What a case can propose, and who must agree or be told."
        actions={addButton("sanction", "New action")}
        rows={sanctions}
        config={definitionConfig<DisciplinaryAction>()}
        columns={sanctionColumns}
        rowKey={(row) => row.id}
        onOpen={(row) => { retire.reset(); setViewing({ kind: "sanction", row }); }}
        noun={["action", "actions"]}
        exportName={{ file: "conduct-actions", title: "Conduct Actions" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canEdit ? "None yet. Add the first, e.g. Verbal warning." : "Leadership has not set any up yet."}
      />

      {viewing && (
        <DetailsModal
          title={viewing.row.name}
          headline={{ label: viewing.kind === "category" ? "Kind of behaviour" : labelFrom(SANCTION_TYPES, viewing.row.action_type), value: `Severity ${severityLabel(viewing.row.severity_level)}`, note: `${viewing.row.incident_count} case${viewing.row.incident_count === 1 ? "" : "s"}` }}
          status={active(viewing.row)}
          facts={
            viewing.kind === "sanction"
              ? [
                  ["Lasts", viewing.row.duration_days ? `${viewing.row.duration_days} days` : "Not timed"],
                  ["Parent told", viewing.row.requires_parent_notification ? "Yes" : "No"],
                  ["Leadership approval", viewing.row.requires_principal_approval || viewing.row.severity_level >= 4 ? "Needed" : "Not needed"],
                ]
              : [["Colour", <span key="c" className="inline-block h-3 w-8 rounded" style={{ backgroundColor: viewing.row.color_code }} />]]
          }
          sections={[{ title: viewing.kind === "category" ? "What it covers" : "What it involves", body: viewing.row.description }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canEdit && (
                <button type="button" disabled={retire.isPending} onClick={() => retire.mutate(viewing)} className={`mr-auto rounded-md px-3 py-2 text-sm font-medium ${viewing.row.is_active ? "text-red-700 hover:bg-red-50" : "text-gray-700 hover:bg-gray-100"}`}>
                  {viewing.row.is_active ? "Retire it" : "Use it again"}
                </button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canEdit && (
                <button type="button" onClick={() => setEditing(viewing.kind === "category" ? { kind: "category", row: viewing.row } : { kind: "sanction", row: viewing.row })} className="btn btn-primary gap-1.5">
                  <Pencil className="h-4 w-4" /> Change
                </button>
              )}
            </>
          }
        >
          {retire.isError && errorLine(retire.error, "That could not be saved.")}
        </DetailsModal>
      )}
      {editing?.kind === "category" && <CategoryForm category={editing.row} onClose={() => setEditing(null)} onDone={(row) => { setEditing(null); done(`${row.name} saved.`); }} />}
      {editing?.kind === "sanction" && <SanctionForm sanction={editing.row} onClose={() => setEditing(null)} onDone={(row) => { setEditing(null); done(`${row.name} saved.`); }} />}
    </div>
  );
}
